import { CommonModule, DOCUMENT, isPlatformBrowser } from '@angular/common';
import { HttpResponse } from '@angular/common/http';
import {
    afterNextRender,
    Component,
    effect,
    inject,
    Input,
    OnInit,
    PLATFORM_ID,
    Renderer2,
    signal,
    WritableSignal,
} from '@angular/core';
import {
    NavigationEnd,
    NavigationStart,
    Router,
    RouterOutlet,
} from '@angular/router';
import {
    BabylonFooterContactI,
    BabylonFooterLinksI,
    BabylonFooterLogosI,
    BabylonHeaderBookShowI,
    BabylonHeaderClearI,
    BabylonHeaderMenuCenterI,
    BabylonHeaderMenuLogoCenterI,
    BabylonHeaderMenuShowI,
    BabylonLanguageModalI,
    BabylonMenuButtonI,
    BabylonModalMultipleMotorsI,
    BabylonNewsletterI,
    BabylonStaticFooterI,
    BabylonStaticFooterV2I,
    BodyComponent,
    MapperService,
    PageConfig,
    Utils,
} from 'ctt-babylon';
import { catchError, filter, forkJoin, map, of, shareReplay, tap } from 'rxjs';
import { LayoutComponent } from './components/layout/layout.component';
import { environment } from './environments/environment';
import { ApiService } from './services/api/api.service';
import { LayoutService } from './services/layout/layout.service';
import { RoutesService } from './services/routes/routes.service';
import { TEMPLATES } from './services/routes/templates';
import { SiteConfigService } from './services/site-config/site-config.service';

type GLOBAL_COMPONENTS =
    | BabylonMenuButtonI
    | BabylonLanguageModalI
    | BabylonStaticFooterI
    | BabylonNewsletterI
    | BabylonFooterLinksI
    | BabylonFooterLogosI
    | BabylonStaticFooterV2I;

@Component({
    selector: 'app-root',
    standalone: true,
    imports: [CommonModule, RouterOutlet, LayoutComponent],
    templateUrl: './app.component.html',
    styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit {
    @Input() fuentesGoogleValidas: string[] = [];
    private readonly platformId = inject(PLATFORM_ID);
    private readonly document = inject(DOCUMENT);
    isHomePage = signal(false);
    header: WritableSignal<
        | BabylonHeaderBookShowI
        | BabylonHeaderClearI
        | BabylonHeaderMenuCenterI
        | BabylonHeaderMenuLogoCenterI
        | BabylonHeaderMenuShowI
        | undefined
    > = signal(undefined);

    languages: WritableSignal<BabylonLanguageModalI | undefined> =
        signal(undefined);
    newsletter: WritableSignal<BabylonNewsletterI | undefined> =
        signal(undefined);
    modalMultipleMotors: WritableSignal<
        BabylonModalMultipleMotorsI | undefined
    > = signal(undefined);

    private staticFooter: BodyComponent<
        BabylonStaticFooterI | BabylonStaticFooterV2I
    > = {} as BodyComponent<BabylonStaticFooterI | BabylonStaticFooterV2I>;

    private footerLogos: BodyComponent<BabylonFooterLogosI> =
        {} as BodyComponent<BabylonFooterLogosI>;

    private footerLinks: BodyComponent<BabylonFooterLinksI> =
        {} as BodyComponent<BabylonFooterLinksI>;

    constructor(
        private readonly apiService: ApiService,
        private readonly siteSvc: SiteConfigService,
        private readonly mapperSvc: MapperService,
        private readonly layoutSvc: LayoutService,
        private readonly renderer: Renderer2,
        private readonly routesSvc: RoutesService,
        private router: Router
    ) {
        afterNextRender(() => {
            if (!isPlatformBrowser(this.platformId)) return;
            this.initBrowserOnly();
        });

        effect(() => {
            const loaded = this.layoutSvc.firstPageLoaded();
            if (loaded) this.setPageHeadConfig();
        });

        this.router.events
            .pipe(filter((ev) => ev instanceof NavigationEnd))
            .subscribe(() => {
                const pageId = this.routesSvc.getPageId();
                this.isHomePage.set(
                    this.siteSvc.pages?.find((p) => p.id === pageId)?.home === 1
                );

                this.setPageHeadConfig();
                const cfg = this.siteSvc.webConfigInstant;
                if (cfg) this.setCommonScripts(cfg);
            });
    }

    private initBrowserOnly(): void {
        const cfg = this.siteSvc.webConfigInstant;
        if (cfg) {
            if (cfg.favIcons?.length) this.setFavicon();
            this.setCommonScripts(cfg);
            this.loadExternalIcons((cfg?.css as any).icons as string);
        }

        this.getDataApi();

        this.routesSvc.addRoutingEvents();

        this.router.events
            .pipe(filter((e) => e instanceof NavigationEnd))
            .subscribe(() => {
                const pageId = this.routesSvc.getPageId();
                this.isHomePage.set(
                    this.siteSvc.pages?.find((p) => p.id === pageId)?.home === 1
                );

                const latestCfg = this.siteSvc.webConfigInstant;
                if (latestCfg) this.setCommonScripts(latestCfg);
            });

        let isRedirecting = false;
        this.router.events.subscribe((event) => {
            if (event instanceof NavigationStart && /[A-Z]/.test(event.url)) {
                const fixedURL = environment.normalizeUrlsToLowercase
                    ? event.url.toLocaleLowerCase()
                    : event.url;
                if (event.url !== fixedURL) {
                    isRedirecting = true;
                    this.router
                        .navigateByUrl(fixedURL, { replaceUrl: true })
                        .then(() => {
                            isRedirecting = false;
                        });
                }
            }
        });
    }

    ngOnInit(): void {
        this.injectGoogleFontsFromVariables();
        const pageId = this.routesSvc.getPageId();
        this.isHomePage.set(
            this.siteSvc.pages?.find((page) => page.id === pageId)?.home === 1
        );
    }

    getDataApi() {
        const isBrowser = isPlatformBrowser(this.platformId);
        if (!isBrowser) return;

        const globalComponents$ = this.siteSvc
            .getPageConfig<
                GLOBAL_COMPONENTS | undefined
            >(this.routesSvc.getPageIdByTemplate(TEMPLATES.GLOBAL_COMPONENTS), 0)
            .pipe(
                map((response) =>
                    response instanceof HttpResponse ? response.body : response
                ),
                filter(
                    (
                        config
                    ): config is PageConfig<GLOBAL_COMPONENTS | undefined> =>
                        config !== null
                ),
                tap((response) => this.manageFooterInfo(response)),
                catchError(() => of(undefined)),
                shareReplay(1)
            );

        const engine$ = this.apiService
            .getEngine(
                this.siteSvc.selectedLanguageInstant?.id,
                'site',
                environment.siteId
            )
            .pipe(
                filter((data) => data !== null),
                map((data) => Utils.convertObjectKeysToCamelCase(data)),
                map((data) => this.mapperSvc.mapEngine(data)),
                tap((data) => this.layoutSvc.setEngineData(data)),
                catchError(() => of(undefined))
            );

        forkJoin([globalComponents$, engine$])
            .pipe(
                tap(([globalComponents, engine]) => {
                    if (!globalComponents) return;

                    const body = (globalComponents as any).body || [];
                    const bodyExtra = (globalComponents as any).bodyExtra || [];

                    this.languages.set(
                        this.mapperSvc.mapLanguageModal(
                            Utils.findComponent(
                                body,
                                'languagesmodal'
                            ) as BabylonLanguageModalI
                        )
                    );

                    this.newsletter.set(
                        this.mapperSvc.mapNewsletterModal(
                            Utils.findComponent(
                                body,
                                'newslettermodal'
                            ) as BabylonNewsletterI
                        )
                    );

                    this.modalMultipleMotors.set(
                        this.mapperSvc.mapModalMultipleMotors(
                            Utils.findComponent(
                                body,
                                'modalmultiplemotors'
                            ) as BabylonModalMultipleMotorsI
                        )
                    );

                    this.header.set(
                        this.mapperSvc.mapHeader(
                            Utils.findComponent(bodyExtra, 'headerbuttons'),
                            Utils.findComponent(
                                body,
                                'nav'
                            ) as BabylonMenuButtonI
                        )
                    );
                })
            )
            .subscribe();
    }

    manageFooterInfo(
        response: PageConfig<GLOBAL_COMPONENTS | undefined>
    ): void {
        const footer = response.body.find((component) =>
            component.name?.toLowerCase()?.includes('staticfooter')
        ) as BodyComponent<any>;

        if (footer) {
            this.staticFooter = footer;
            this.layoutSvc.setFooter({
                ...this.staticFooter,
                props: this.staticFooter?.name?.toLowerCase()?.includes('v2')
                    ? this.mapperSvc.mapStaticFooterv2(footer.props)
                    : this.mapperSvc.mapStaticFooter(footer.props),
            });
        }

        const footerLogos = response.body.find((component) =>
            component.name?.toLowerCase()?.includes('footerlogos')
        ) as BodyComponent<BabylonFooterLogosI>;

        if (footerLogos) {
            this.footerLogos = footerLogos;
            this.layoutSvc.setFooterLogosData({
                ...this.footerLogos,
                props: this.mapperSvc.mapFooterLogos(footerLogos.props),
            });
        }

        const footerLinks = response.body.find((component) =>
            component.name?.toLowerCase()?.includes('footerlinks')
        ) as BodyComponent<BabylonFooterLinksI>;

        if (footerLinks) {
            this.footerLinks = footerLinks;
            this.layoutSvc.setFooterLinksData({
                ...this.footerLinks,
                props: this.mapperSvc.mapFooterLinks(footerLinks.props),
            });
        }

        const footerContact = response.body.find((component) =>
            component.name?.toLowerCase()?.includes('footercontact')
        ) as BodyComponent<BabylonFooterContactI>;

        if (footerContact) {
            this.layoutSvc.setFooterContactData({
                ...footerContact,
                props: this.mapperSvc.mapFooterContact(footerContact.props),
            });
        }
    }

    manageInitialConfig(): void {
        // La lógica se ha movido a un effect en el constructor para que sea reactiva.
    }

    loadExternalIcons(suffixUrl?: string | null): void {
        if (isPlatformBrowser(this.platformId)) {
            if (suffixUrl) {
                Utils.loadStyle$(
                    `${environment.baseUrl}/${suffixUrl}`
                ).subscribe();
            }
        }
    }

    setFavicon(): void {
        if (isPlatformBrowser(this.platformId)) {
            document.querySelectorAll("link[rel='icon']").forEach((favicon) => {
                this.renderer.removeChild(document.head, favicon);
            });

            const faviconUrl =
                this.siteSvc.webConfigInstant?.favIcons?.[1]?.toString() ??
                'assets/babylon/favicons/favicon.ico';

            const newFavicon = this.renderer.createElement('link');
            this.renderer.setAttribute(newFavicon, 'rel', 'icon');
            this.renderer.setAttribute(newFavicon, 'type', 'image/x-icon');
            this.renderer.setAttribute(newFavicon, 'href', faviconUrl);

            this.renderer.appendChild(document.head, newFavicon);
        }
    }

    setCommonScripts(config: any) {
        if (isPlatformBrowser(this.platformId)) {
            document
                .querySelectorAll('script[data-from-config]')
                .forEach((el) => this.renderer.removeChild(document.head, el));

            if (this.isHomePage()) {
                (config.scripts ?? []).forEach((raw: any) =>
                    this.addSafeScript(raw, 'from-config')
                );
            }

            const gtms: string[] = Array.isArray(config.commonScripts?.GTM)
                ? config.commonScripts.GTM.filter((id: string) => !!id)
                : [];

            gtms.forEach((gtmId) => this.injectGtm(gtmId));
        }
    }

    setPageHeadConfig(): void {
        const config = this.layoutSvc.headInstant;

        if (!isPlatformBrowser(this.platformId) || !config) return;

        this.document
            .querySelectorAll('script[data-from-page-config]')
            .forEach((el) => this.renderer.removeChild(this.document.head, el));

        (config.scripts ?? [])
            .map((s: any) => (typeof s === 'string' ? s : s.script))
            .filter(Boolean)
            .forEach((raw) => this.addSafeScript(raw, 'from-config'));

        const links = this.document.querySelectorAll('link');
        links.forEach((link) => {
            if (
                link.getAttribute('rel') === 'canonical' ||
                link.getAttribute('rel') === 'alternate'
            ) {
                this.renderer.removeChild(this.document.head, link);
            }
        });

        const metas = this.document.querySelectorAll('meta');
        metas.forEach((meta) => {
            if (
                meta.getAttribute('charset') !== 'utf-8' &&
                meta.getAttribute('name') !== 'viewport' &&
                meta.getAttribute('name') !== 'description'
            ) {
                this.renderer.removeChild(this.document.head, meta);
            }
        });

        this.removeHeadComments();

        let group = '';

        config?.metas?.forEach((enlace: any) => {
            if (enlace['group'] !== group) {
                this.renderer.appendChild(
                    this.document.head,
                    this.renderer.createComment(enlace['group'].toUpperCase())
                );
                group = enlace['group'];
            }
            let linkElement: any;
            const metaDescription = this.document.querySelector(
                'meta[name="description"]'
            );
            switch (enlace['type']) {
                case 'link':
                    linkElement = this.renderer.createElement(
                        'link'
                    ) as HTMLLinkElement;
                    linkElement.setAttribute('rel', 'preload');
                    break;
                case 'meta':
                    if (enlace['name'] === 'description' && metaDescription) {
                        this.renderer.setAttribute(
                            metaDescription,
                            'content',
                            enlace['content']
                        );
                        break;
                    }
                    linkElement = this.renderer.createElement(
                        'meta'
                    ) as HTMLMetaElement;
                    break;
                case 'title':
                    linkElement = this.document.querySelector('title');

                    linkElement.text = enlace['content'];
                    break;

                default:
                    break;
            }
            if (
                (enlace['type'] !== 'title' &&
                    enlace['name'] !== 'description') ||
                (enlace['name'] === 'description' && metaDescription === null)
            ) {
                Object.keys(enlace).forEach((key) => {
                    if (key !== 'group' && key !== 'type') {
                        this.renderer.setAttribute(
                            linkElement,
                            key,
                            enlace[key]
                        );
                    }
                });
            }

            if (
                (enlace['name'] !== 'viewport' &&
                    enlace['type'] !== 'title' &&
                    enlace['name'] !== 'charset' &&
                    enlace['name'] !== 'description') ||
                (enlace['name'] === 'description' &&
                    metaDescription === null &&
                    linkElement)
            ) {
                this.renderer.appendChild(this.document.head, linkElement);
            }
        });
    }

    private addSafeScript(
        raw: string | { script: string },
        dataAttr: string
    ): void {
        const html = (typeof raw === 'string' ? raw : raw.script).trim();
        const scriptEl = this.renderer.createElement('script');

        if (html.startsWith('<script')) {
            const tmp = this.document.createElement('div');
            tmp.innerHTML = html;
            const original = tmp.querySelector('script')!;

            Array.from(original.attributes).forEach((attr) =>
                this.renderer.setAttribute(scriptEl, attr.name, attr.value)
            );
            scriptEl.textContent = original.textContent;
        } else {
            const looksLikeJson = html.startsWith('{') || html.startsWith('[');
            this.renderer.setAttribute(
                scriptEl,
                'type',
                looksLikeJson ? 'application/ld+json' : 'text/javascript'
            );
            scriptEl.textContent = html;
        }

        this.renderer.setAttribute(scriptEl, `data-${dataAttr}`, 'true');
        this.renderer.appendChild(this.document.head, scriptEl);
    }

    private injectGtm(gtmId: string): void {
        const gtm = this.renderer.createElement('script');
        gtm.textContent = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
    new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
    j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
    'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
  })(window,document,'script','dataLayer','GTM-${gtmId}');`;
        this.renderer.appendChild(
            this.document.head,
            this.renderer.createComment('Google Tag Manager')
        );
        this.renderer.appendChild(this.document.head, gtm);
        this.renderer.appendChild(
            this.document.head,
            this.renderer.createComment('End Google Tag Manager')
        );

        const ns = this.renderer.createElement('noscript');
        ns.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=GTM-${gtmId}"
      height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
        this.renderer.appendChild(
            this.document.body,
            this.renderer.createComment('Google Tag Manager (noscript)')
        );
        this.renderer.appendChild(this.document.body, ns);
        this.renderer.appendChild(
            this.document.body,
            this.renderer.createComment('End Google Tag Manager (noscript)')
        );
    }

    removeHeadComments() {
        if (isPlatformBrowser(this.platformId)) {
            const head = document.head;
            const treeWalker = document.createTreeWalker(
                head,
                NodeFilter.SHOW_COMMENT,
                null
            );

            const comments = [];

            while (treeWalker.nextNode()) {
                comments.push(treeWalker.currentNode);
            }

            for (const comment of comments) {
                comment?.parentNode?.removeChild(comment);
            }
        }
    }

    injectGoogleFontsFromVariables(): void {
        if (isPlatformBrowser(this.platformId)) {
            const computedStyles = getComputedStyle(
                this.document.documentElement
            );
            const fontVariables = [
                '--font-title',
                '--font-pretitle',
                '--font-text',
                '--font-claim',
            ];

            fontVariables.forEach((varName) => {
                const value = computedStyles.getPropertyValue(varName).trim();
                const match = value.match(/^['"]?([^'",]+)['"]?/);
                const fontName = match ? match[1] : null;

                if (
                    fontName &&
                    this.fuentesGoogleValidas.includes(fontName) // solo si es fuente de Google
                ) {
                    const formattedFont = fontName.replace(/ /g, '+');
                    const id = `font-${formattedFont}`;

                    if (!this.document.getElementById(id)) {
                        const link = this.document.createElement('link');
                        link.id = id;
                        link.rel = 'stylesheet';
                        link.href = `https://fonts.googleapis.com/css2?family=${formattedFont}&display=swap`;
                        this.document.head.appendChild(link);
                    }
                }
            });
        }
    }
}
